import { TFunction } from 'i18next'
import { z } from 'zod'

import { ZIP_CODE_CA_REGEX, ZIP_CODE_US_REGEX } from '@/constants/regex'
import { isValidLink } from '@/utils/link'

import { isValidPhoneInput } from '../../shared/person-table.utils'
import { DEFAULT_PLACE_TIMEZONE, PLACE_TIMEZONES } from '../places.timezones'

export const PLACE_LINK_MAX_LENGTH = 255

export const PlaceCountrySchema = z.enum(['US', 'CA'])

export const PlaceCountry = PlaceCountrySchema.enum

export type PlaceCountry = z.infer<typeof PlaceCountrySchema>

export const makePlaceFormSchema = (t: TFunction<['organisation']>) =>
  z
    .object({
      address: z.string().trim().min(1, t('organisation:places.create.validation.addressRequired')),
      city: z.string().optional(),
      code: z.string().trim().min(1, t('organisation:places.create.validation.codeRequired')),
      country: PlaceCountrySchema,
      email: z
        .string()
        .trim()
        .optional()
        .refine(
          (value) => !value || z.email().safeParse(value).success,
          t('organisation:places.create.validation.emailInvalid'),
        ),
      link: z
        .string()
        .trim()
        .max(
          PLACE_LINK_MAX_LENGTH,
          t('organisation:places.create.validation.linkMaxLength', { max: PLACE_LINK_MAX_LENGTH }),
        )
        .optional()
        .refine(
          (value) => !value || isValidLink(value),
          t('organisation:places.create.validation.linkInvalid'),
        ),
      logo: z.array(z.instanceof(File)).optional(),
      name: z.string().trim().min(1, t('organisation:places.create.validation.nameRequired')),
      phone: z
        .string()
        .min(1, t('organisation:places.create.validation.phoneRequired'))
        .refine(isValidPhoneInput, t('organisation:places.create.validation.phoneInvalid')),
      postalCode: z
        .string()
        .trim()
        .min(1, t('organisation:places.create.validation.postalCodeRequired')),
      state: z.string().nullish(),
      timezone: z.enum(Object.values(PLACE_TIMEZONES) as [string, ...string[]]),
    })
    .superRefine((data, ctx) => {
      if (!data.postalCode) return

      const isValid =
        data.country === PlaceCountry.CA
          ? ZIP_CODE_CA_REGEX.test(data.postalCode)
          : ZIP_CODE_US_REGEX.test(data.postalCode)

      if (!isValid) {
        ctx.addIssue({
          code: 'custom',
          message:
            data.country === PlaceCountry.CA
              ? t('organisation:places.create.validation.postalCodeInvalidCa')
              : t('organisation:places.create.validation.postalCodeInvalidUs'),
          path: ['postalCode'],
        })
      }
    })

export type PlaceFormData = z.infer<ReturnType<typeof makePlaceFormSchema>>

export const placeFormDefaultValues: PlaceFormData = {
  address: '',
  city: '',
  code: '',
  country: PlaceCountry.US,
  email: '',
  link: '',
  logo: [],
  name: '',
  phone: '',
  postalCode: '',
  state: '',
  timezone: DEFAULT_PLACE_TIMEZONE,
}
